/* iter195 — Field Completion Details — READ-ONLY.
   ------------------------------------------------------------
   Single source of truth for surfacing EVERY field a technician entered
   during job completion. Reused from:
     - Admin Schedule Edit Shift Sheet
     - Admin Invoice detail drawer
     - Customer History job details
     - Billing Missing drawer

   Contract:
     - `completionId` : ObjectId string; component fetches the doc.
     - `renderHeader` : when true, shows title + submitted-by pill.
     - Empty fields are hidden (no visual clutter). If tech filled it in,
       office sees it.
   ------------------------------------------------------------ */

import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  ClipboardList, User, PhoneCall, MapPin, Wrench, MessageSquare,
  AlertTriangle, CheckCircle2, DollarSign, FileText, Camera, Package,
  Star, ShieldAlert,
} from 'lucide-react';
import { Badge } from './ui/badge';
import { formatPaymentDisposition, formatInvoiceStatus } from '../lib/paymentFmt';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmtDT = (iso) => { if (!iso) return ''; try { return new Date(iso).toLocaleString(); } catch { return iso; } };

/** iter195 — Small subheader with an icon + label + optional right-aligned pill. */
const SectionHeader = ({ icon: Icon, label, pill }) => (
  <div className="flex items-center justify-between border-b border-[#262626] pb-1 mb-2">
    <p className="text-[#D4AF37] text-[11px] uppercase tracking-wider font-bold flex items-center gap-1.5">
      <Icon className="w-3.5 h-3.5" /> {label}
    </p>
    {pill}
  </div>
);

/** Skip rendering when value is falsy / empty. Preserves whitespace. */
const Row = ({ label, value, testid }) => {
  if (value === null || value === undefined) return null;
  const v = typeof value === 'string' ? value.trim() : value;
  if (v === '' || v === false) return null;
  return (
    <div className="text-sm" data-testid={testid}>
      <span className="text-[#A3A3A3] text-xs uppercase tracking-wider font-semibold">{label}</span>
      <p className="text-white mt-0.5 whitespace-pre-wrap break-words">{String(v)}</p>
    </div>
  );
};

const BadgeChip = ({ children, tone = 'gold', testid }) => {
  const tones = {
    gold:  'bg-[#D4AF37]/15 text-[#D4AF37] border-[#D4AF37]/40',
    amber: 'bg-amber-500/15 text-amber-300 border-amber-500/40',
    red:   'bg-red-500/15 text-red-300 border-red-500/40',
    blue:  'bg-blue-500/15 text-blue-300 border-blue-500/40',
    green: 'bg-green-500/15 text-green-300 border-green-500/40',
    grey:  'bg-[#262626] text-[#C9C9C9] border-[#404040]',
  };
  return (
    <span data-testid={testid} className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 border rounded ${tones[tone] || tones.gold}`}>
      {children}
    </span>
  );
};

/** Compute the review badges from the completion doc. Mirrors backend
 *  `_completion_flags` — kept in sync so we don't need a second round-trip
 *  when the caller already has the completion in hand. */
export const computeCompletionFlags = (comp) => {
  if (!comp) return {};
  const bc = comp.basic_checklist || {};
  const pd = comp.payment_disposition || {};
  const invStatus = String(comp.invoice_status || '').toLowerCase();
  const opps = new Set(comp.opportunity_flags || []);
  const cs = bc.customer_satisfied;
  const notSatisfied = typeof cs === 'string' ? (cs === 'no' || cs === 'na') : cs === false;
  const followupTxt = (comp.recommended_followups || '').trim();
  const followupStatus = String(comp.followup_status || '').toLowerCase();
  const paymentNotes = ((pd && pd.notes) || '').trim();
  const photos = (comp.equipment || []).flatMap((e) => e.photo_urls || []);
  return {
    follow_up_recommended: !!followupTxt && !['closed', 'declined'].includes(followupStatus),
    return_visit_needed: ['return_visit_needed', 'return_visit'].includes(invStatus),
    needs_estimate: opps.has('estimate_scheduled') || !!((comp.quick_equipment || {}).major_concern || '').trim(),
    customer_not_satisfied: notSatisfied,
    deferred_payment: ['deferred_payment', 'deferred_payment_approved', 'deferred_unable_collect', 'customer_not_available'].includes(invStatus),
    has_payment_notes: !!paymentNotes,
    warranty_review: ['hsp_warranty_claim', 'nexflow_warranty', 'warranty', 'hsp'].includes(invStatus),
    office_review_needed: ['office_review_required', 'save_draft'].includes(invStatus),
    has_photos: photos.length > 0,
    photo_count: photos.length,
  };
};

/** Compact badge strip. Also exported so shift/invoice list rows can render
 *  the same pills without pulling in the full details view. */
export const CompletionFlagBadges = ({ flags, testidPrefix = 'fcd' }) => {
  if (!flags) return null;
  const strip = [];
  if (flags.follow_up_recommended) strip.push({ tone: 'gold',  label: 'Follow-Up Recommended', tid: 'follow-up-recommended' });
  if (flags.return_visit_needed)   strip.push({ tone: 'blue',  label: 'Return Visit Needed', tid: 'return-visit-needed' });
  if (flags.needs_estimate)        strip.push({ tone: 'gold',  label: 'Needs Estimate', tid: 'needs-estimate' });
  if (flags.customer_not_satisfied) strip.push({ tone: 'red', label: 'Customer Not Satisfied', tid: 'customer-not-satisfied' });
  if (flags.deferred_payment)      strip.push({ tone: 'amber', label: 'Deferred Payment', tid: 'deferred-payment' });
  if (flags.has_payment_notes)     strip.push({ tone: 'gold',  label: 'Payment Notes', tid: 'payment-notes' });
  if (flags.warranty_review)       strip.push({ tone: 'blue',  label: 'Warranty / HSP Review', tid: 'warranty-review' });
  if (flags.office_review_needed)  strip.push({ tone: 'red',   label: 'Office Review Needed', tid: 'office-review-needed' });
  if (flags.has_photos)            strip.push({ tone: 'grey',  label: `${flags.photo_count} Photo${flags.photo_count > 1 ? 's' : ''}`, tid: 'has-photos' });
  if (strip.length === 0) return null;
  return (
    <div data-testid={`${testidPrefix}-badges`} className="flex flex-wrap gap-1.5">
      {strip.map((b) => (
        <BadgeChip key={b.tid} tone={b.tone} testid={`${testidPrefix}-badge-${b.tid}`}>{b.label}</BadgeChip>
      ))}
    </div>
  );
};

const CS_LABEL = { yes: 'Yes', no: 'No', na: 'Not Sure' };

/** Main read-only view. */
export const FieldCompletionDetails = ({ completionId, renderHeader = true, className = '' }) => {
  const [comp, setComp] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!completionId) { setComp(null); return; }
    let alive = true;
    setLoading(true); setError(null);
    axios.get(`${API}/admin/job-completions/${completionId}`, { withCredentials: true })
      .then((r) => { if (alive) setComp(r.data); })
      .catch((e) => { if (alive) setError(e?.response?.data?.detail || 'Failed to load completion'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [completionId]);

  if (!completionId) return null;
  if (loading) return <p className="text-[#A3A3A3] text-sm" data-testid="fcd-loading">Loading field completion…</p>;
  if (error) return <p className="text-red-300 text-sm" data-testid="fcd-error">{error}</p>;
  if (!comp) return null;

  const bc = comp.basic_checklist || {};
  const qe = comp.quick_equipment || {};
  const pd = comp.payment_disposition || {};
  const hsp = comp.hsp || {};
  const equipment = Array.isArray(comp.equipment) ? comp.equipment : [];
  const photos = equipment.flatMap((e) => (e.photo_urls || []).map((u) => ({ url: u, equipment: e })));
  const flags = computeCompletionFlags(comp);
  const cs = bc.customer_satisfied;
  const csLabel = typeof cs === 'string' ? (CS_LABEL[cs] || '') : (cs === true ? 'Yes' : cs === false ? 'No' : '');

  return (
    <div data-testid="field-completion-details" className={`bg-[#141414] border border-[#D4AF37]/30 rounded-md p-4 space-y-4 ${className}`}>
      {renderHeader && (
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <p className="text-[#D4AF37] text-xs uppercase tracking-wider font-bold flex items-center gap-2">
              <ClipboardList className="w-4 h-4" /> Field Completion Details
            </p>
            <p className="text-white text-sm mt-1">
              Submitted by <span className="font-bold">{comp.employee_name || comp.submitted_by_name || 'tech'}</span>
              {comp.submitted_at && <> · {fmtDT(comp.submitted_at)}</>}
            </p>
          </div>
          <Badge className="bg-[#D4AF37]/15 text-[#D4AF37] border-[#D4AF37]/40 uppercase text-[10px] font-bold">
            {(comp.status || '').replace(/_/g, ' ')}
          </Badge>
        </div>
      )}

      <CompletionFlagBadges flags={flags} testidPrefix="fcd" />

      {/* Job header */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Row label="Customer"     value={comp.customer_name}          testid="fcd-customer" />
        <Row label="Address"      value={comp.address}                testid="fcd-address" />
        <Row label="Work Order #" value={comp.work_order_number}      testid="fcd-wo" />
        <Row label="Service Type" value={comp.service_type}           testid="fcd-service-type" />
        <Row label="Shift"        value={comp.shift_title}            testid="fcd-shift" />
        <Row label="Date"         value={comp.date}                   testid="fcd-date" />
      </div>

      {/* Work summary */}
      {(comp.work_performed || comp.materials_used || comp.hours_worked) && (
        <div>
          <SectionHeader icon={Wrench} label="Work Summary" />
          <div className="space-y-2">
            <Row label="Work Performed"  value={comp.work_performed}  testid="fcd-work-performed" />
            <Row label="Materials Used"  value={comp.materials_used}  testid="fcd-materials-used" />
            <Row label="Hours Worked"    value={comp.hours_worked}    testid="fcd-hours-worked" />
          </div>
        </div>
      )}

      {/* Recommended follow-ups — always prominent when present */}
      {(comp.recommended_followups || '').trim() && (
        <div data-testid="fcd-followup-block" className="bg-[#1a1505] border border-[#D4AF37]/50 rounded p-3">
          <SectionHeader
            icon={MessageSquare}
            label="Recommended Follow-Up"
            pill={
              <BadgeChip tone={flags.follow_up_recommended ? 'gold' : 'grey'}>
                {(comp.followup_status || 'new').toUpperCase()}
              </BadgeChip>
            }
          />
          <p className="text-white text-sm whitespace-pre-wrap" data-testid="fcd-followup-text">
            {comp.recommended_followups}
          </p>
          {(comp.followup_notes || []).length > 0 && (
            <div className="mt-3 space-y-1.5">
              <p className="text-[#A3A3A3] text-[10px] uppercase tracking-wider font-bold">Office Notes</p>
              {(comp.followup_notes || []).map((n, i) => (
                <div key={i} className="text-xs border-l-2 border-[#D4AF37]/40 pl-2">
                  <p className="text-white whitespace-pre-wrap">{n.note}</p>
                  <p className="text-[#525252]">{n.by_name} · {fmtDT(n.at)}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Basic checklist / customer satisfaction */}
      {(csLabel || bc.cleaned_work_area || bc.tested_repair || bc.explained_work) && (
        <div>
          <SectionHeader icon={Star} label="Customer Satisfaction & Checklist" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <Row label="Customer Satisfied?" value={csLabel} testid="fcd-customer-satisfied" />
            <Row label="Cleaned Work Area"   value={bc.cleaned_work_area ? 'Yes' : ''} />
            <Row label="Tested Repair"      value={bc.tested_repair ? 'Yes' : ''} />
            <Row label="Explained Work"     value={bc.explained_work ? 'Yes' : ''} />
            <Row label="Review Request Sent" value={comp.send_review_request ? 'Yes' : ''} />
            <Row label="Signed By"          value={comp.customer_signature_name} testid="fcd-signature" />
          </div>
        </div>
      )}

      {/* Quick equipment notes */}
      {(qe.water_heater_age || qe.water_heater_size || qe.major_concern) && (
        <div>
          <SectionHeader icon={Package} label="Quick Equipment Notes" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <Row label="Water Heater Age"  value={qe.water_heater_age}  testid="fcd-wh-age" />
            <Row label="Water Heater Size" value={qe.water_heater_size} testid="fcd-wh-size" />
            <Row label="Major Concern"     value={qe.major_concern}     testid="fcd-major-concern" />
          </div>
        </div>
      )}

      {/* Equipment / assets */}
      {equipment.length > 0 && (
        <div>
          <SectionHeader icon={Package} label={`Equipment (${equipment.length})`} />
          <div className="space-y-2">
            {equipment.map((e, i) => (
              <div key={i} className="bg-[#0A0A0A] border border-[#262626] rounded p-2 space-y-1" data-testid={`fcd-equipment-${i}`}>
                <p className="text-white text-sm font-bold">
                  {e.type || 'Item'}{e.brand ? ` · ${e.brand}` : ''}{e.model ? ` ${e.model}` : ''}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 text-xs">
                  <Row label="Serial"       value={e.serial} />
                  <Row label="Location"     value={e.location} />
                  <Row label="Condition"    value={e.condition} />
                  <Row label="Installed"    value={e.install_date} />
                  <Row label="Warranty To"  value={e.warranty_until} />
                  <Row label="Recommended Replace" value={e.recommended_replacement_date} />
                </div>
                {(e.notes || '').trim() && (
                  <p className="text-xs text-white border-l-2 border-[#D4AF37]/40 pl-2 whitespace-pre-wrap">{e.notes}</p>
                )}
                {(e.photo_urls || []).length > 0 && (
                  <div className="flex flex-wrap gap-2 pt-1">
                    {e.photo_urls.map((u, j) => (
                      <a key={j} href={u} target="_blank" rel="noreferrer" className="block" data-testid={`fcd-photo-${i}-${j}`}>
                        <img src={u} alt="equipment" className="w-16 h-16 object-cover rounded border border-[#262626]" />
                      </a>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Photos not attached to an equipment entry (future-proof) */}
      {photos.length > 0 && equipment.length === 0 && (
        <div>
          <SectionHeader icon={Camera} label={`Photos (${photos.length})`} />
          <div className="flex flex-wrap gap-2">
            {photos.map((p, i) => (
              <a key={i} href={p.url} target="_blank" rel="noreferrer">
                <img src={p.url} alt="job" className="w-20 h-20 object-cover rounded border border-[#262626]" />
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Billing outcome + payment disposition */}
      {(comp.invoice_status || pd.disposition) && (
        <div>
          <SectionHeader icon={DollarSign} label="Payment / Billing Closeout" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <Row label="Billing Outcome"     value={formatInvoiceStatus(comp.invoice_status)} testid="fcd-invoice-status" />
            <Row label="Payment Method"      value={formatPaymentDisposition(pd.disposition, pd.payment_method_detail)} testid="fcd-payment-disposition" />
            <Row label="Method Detail"       value={pd.disposition === 'paid_other' ? pd.payment_method_detail : ''} testid="fcd-payment-method-detail" />
            <Row label="Amount Collected"    value={pd.amount != null && pd.amount !== '' ? `$${Number(pd.amount).toFixed(2)}` : ''} testid="fcd-amount" />
            <Row label="Reference"           value={pd.reference} testid="fcd-payment-reference" />
          </div>
          {(pd.notes || '').trim() && (
            <div className="mt-2 bg-[#1a1505] border border-[#D4AF37]/40 rounded p-2" data-testid="fcd-payment-notes-wrap">
              <p className="text-[#D4AF37] text-[10px] uppercase tracking-wider font-bold mb-1">Tech Payment Notes</p>
              <p className="text-white text-sm whitespace-pre-wrap" data-testid="fcd-payment-notes">{pd.notes}</p>
            </div>
          )}
        </div>
      )}

      {/* HSP / warranty */}
      {(hsp.warranty_company || hsp.claim_number || hsp.deductible || hsp.notes) && (
        <div>
          <SectionHeader icon={ShieldAlert} label="HSP / Warranty" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <Row label="Warranty Company" value={hsp.warranty_company} testid="fcd-hsp-company" />
            <Row label="Claim / WO #"     value={hsp.claim_number} testid="fcd-hsp-claim" />
            <Row label="Deductible"       value={hsp.deductible != null && hsp.deductible !== '' ? `$${Number(hsp.deductible).toFixed(2)}` : ''} />
          </div>
          {(hsp.notes || '').trim() && (
            <p className="text-white text-sm mt-2 whitespace-pre-wrap border-l-2 border-[#D4AF37]/40 pl-2" data-testid="fcd-hsp-notes">{hsp.notes}</p>
          )}
        </div>
      )}

      {/* Opportunity flags */}
      {(comp.opportunity_flags || []).length > 0 && (
        <div>
          <SectionHeader icon={AlertTriangle} label="Opportunity Flags" />
          <div className="flex flex-wrap gap-1.5" data-testid="fcd-opportunity-flags">
            {comp.opportunity_flags.map((f) => (
              <BadgeChip key={f} tone="amber">{String(f).replace(/_/g, ' ')}</BadgeChip>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default FieldCompletionDetails;
